import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { DateRange } from "react-day-picker";
import { Plus } from "lucide-react";
import { useBorrowersList } from "@/hooks/useBorrowersList";
import BorrowersSearch from "@/components/borrower/BorrowersSearch";
import BorrowersTable from "@/components/borrower/BorrowersTable";
import AppStatusFilter from "@/components/borrower/AppStatusFilter";
import WorkflowBoard from "@/components/borrower/workflow/WorkflowBoard";
import WorkflowDateFilter from "@/components/borrower/workflow/WorkflowDateFilter";
import { addReadId, clearReadIds } from "@/store/slices/borrowersSlice";
import { AppDispatch } from "@/store/store";
import { Button } from "@/components/ui/button";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import CountChip from "@/components/common/CountChip";

type ViewMode = "table" | "workflow";
const VIEW_MODE_STORAGE_KEY = "applicationsViewMode";
const DATE_RANGE_STORAGE_KEY = "applicationsDateRange";
// After this long, a stale end date is rolled forward to today (see
// getInitialDateRange) so returning users don't have to manually extend
// their range to include newly-created applications.
const DATE_RANGE_TTL_MS = 8 * 60 * 60 * 1000;

interface StoredDateRange {
  from?: string;
  to?: string;
  savedAt?: number;
}

function getInitialViewMode(): ViewMode {
  return window.localStorage.getItem(VIEW_MODE_STORAGE_KEY) === "table" ? "table" : "workflow";
}

function startOfDay(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

function persistDateRange(range: DateRange | undefined): void {
  const payload: StoredDateRange = range?.from
    ? { from: range.from.toISOString(), to: range.to?.toISOString(), savedAt: Date.now() }
    : {};
  window.localStorage.setItem(DATE_RANGE_STORAGE_KEY, JSON.stringify(payload));
}

function getInitialDateRange(): DateRange | undefined {
  const raw = window.localStorage.getItem(DATE_RANGE_STORAGE_KEY);
  if (raw !== null) {
    try {
      const parsed = JSON.parse(raw) as StoredDateRange;
      if (!parsed.from) {
        // Persisted "cleared" state (Any date).
        return undefined;
      }

      const from = new Date(parsed.from);
      let to = parsed.to ? new Date(parsed.to) : undefined;

      const isStale = !parsed.savedAt || Date.now() - parsed.savedAt > DATE_RANGE_TTL_MS;
      const today = new Date();
      if (isStale && to && startOfDay(to) < startOfDay(today)) {
        // The cached range has expired and its end date has fallen behind —
        // bump it up to today while keeping the user's chosen start date.
        to = today;
        persistDateRange({ from, to });
      }

      return { from, to };
    } catch {
      // Corrupt value — fall through to the default below.
    }
  }
  const today = new Date();
  return { from: today, to: today };
}

const Borrowers: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const [viewMode, setViewModeState] = useState<ViewMode>(getInitialViewMode);
  const [dateRange, setDateRange] = useState<DateRange | undefined>(getInitialDateRange);

  const setViewMode = useCallback((mode: ViewMode) => {
    window.localStorage.setItem(VIEW_MODE_STORAGE_KEY, mode);
    setViewModeState(mode);
  }, []);

  const handleDateRangeChange = useCallback((range: DateRange | undefined) => {
    persistDateRange(range);
    setDateRange(range);
  }, []);

  const {
    borrowers,
    loading,
    pagination,
    filters,
    statusFilterValue,
    rawSearch,
    setRawSearch,
    handleStatusSelect,
    page,
    setPage,
    pageSize,
    setPageSize,
    sortColumn,
    sortDirection,
    handleSortClick,
  } = useBorrowersList(viewMode === "table");

  const prevLoadingRef = useRef(false);
  useEffect(() => {
    if (prevLoadingRef.current && !loading) {
      dispatch(clearReadIds());
    }
    prevLoadingRef.current = loading;
  }, [loading, dispatch]);

  const handleRowClick = useCallback(
    (borrowerId: number) => {
      dispatch(addReadId(borrowerId));
      navigate(`/applications/${borrowerId}`);
    },
    [navigate, dispatch]
  );

  const handleNewApplication = useCallback(() => {
    navigate("/applications/new");
  }, [navigate]);

  return (
    <div>
      {/* Page header */}
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2.5">
          <h1 className="text-2xl font-bold tracking-tight">Applications</h1>
          {viewMode === "table" && !loading && <CountChip count={pagination.count} />}
        </div>

        <Button
          data-testid="new-application-header"
          onClick={handleNewApplication}
          className="gap-2 shadow-sm"
        >
          <Plus className="h-4 w-4" />
          New Application
        </Button>
      </div>

      {/* Filter row — sits on page bg, no card wrapper needed */}
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end">
        <div className="flex flex-1 flex-col gap-1">
          <span className="text-xs font-medium text-muted-foreground">Search</span>
          <BorrowersSearch value={rawSearch} onChange={setRawSearch} />
        </div>
        {viewMode === "table" ? (
          <AppStatusFilter value={statusFilterValue} onStatusSelect={handleStatusSelect} />
        ) : (
          <WorkflowDateFilter value={dateRange} onChange={handleDateRangeChange} />
        )}
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-muted-foreground">View</span>
          <ToggleGroup
            type="single"
            value={viewMode}
            onValueChange={(v) => v && setViewMode(v as ViewMode)}
            variant="outline"
            size="lg"
            className="justify-start"
          >
            <ToggleGroupItem
              value="workflow"
              data-testid="view-toggle-workflow"
              className="px-3 text-xs"
            >
              Workflow
            </ToggleGroupItem>
            <ToggleGroupItem value="table" data-testid="view-toggle-table" className="px-3 text-xs">
              Table
            </ToggleGroupItem>
          </ToggleGroup>
        </div>
      </div>

      {viewMode === "table" ? (
        <BorrowersTable
          borrowers={borrowers}
          loading={loading}
          pagination={pagination}
          page={page}
          pageSize={pageSize}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          onRowClick={handleRowClick}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          onSortClick={handleSortClick}
        />
      ) : (
        <WorkflowBoard filters={filters} dateRange={dateRange} />
      )}
    </div>
  );
};

export default Borrowers;
